import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import ViewLayout from '../../../components/Common/ViewLayout';

interface DriverFormData {
  nome: string;
  cpf: string;
  rg?: string;
  data_nascimento?: string;
  telefone?: string;
  celular: string;
  lid?: string;
  email?: string;
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  cnh_numero: string;
  cnh_categoria?: string;
  cnh_vencimento: string;
  cnh_primeira_habilitacao?: string;
  cnh_pontos_atuais?: number;
  foto_motorista?: string;
  foto_cnh_frente?: string;
  foto_cnh_verso?: string;
  contato_emergencia_nome?: string;
  contato_emergencia_telefone?: string;
  contato_emergencia_parentesco?: string;
  data_admissao?: string;
  data_demissao?: string;
  salario?: number;
  comissao_percentual?: number;
  banco?: string;
  agencia?: string;
  conta?: string;
  pix_chave?: string;
  observacoes?: string;
  status: 'ativo' | 'inativo';
  avaliacao_comportamental?: string;
  numero_viagens_realizadas?: number;
  quilometragem_total?: number;
}

const DriverEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<DriverFormData>({
    nome: '',
    cpf: '',
    celular: '',
    cnh_numero: '',
    cnh_vencimento: '',
    status: 'ativo',
    cnh_pontos_atuais: 0,
    numero_viagens_realizadas: 0,
    quilometragem_total: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    if (id) {
      loadDriverData();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    
    // Se já estiver no formato YYYY-MM-DD, retorna diretamente
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Tenta converter de outros formatos
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const loadDriverData = async () => {
    try {
      setIsLoadingData(true);
      const response = await apiService.getDriver(Number(id)) as ApiResponse<any>;
      
      if (response.success && response.data) {
        const driver = response.data;
        setFormData({
          nome: driver.nome || '',
          cpf: driver.cpf || '',
          rg: driver.rg || '',
          data_nascimento: formatDateForInput(driver.data_nascimento),
          telefone: driver.telefone || '',
          celular: driver.celular || '',
          lid: driver.lid || '',
          email: driver.email || '',
          endereco_rua: driver.endereco_rua || '',
          endereco_numero: driver.endereco_numero || '',
          endereco_complemento: driver.endereco_complemento || '',
          endereco_bairro: driver.endereco_bairro || '',
          endereco_cidade: driver.endereco_cidade || '',
          endereco_estado: driver.endereco_estado || '',
          endereco_cep: driver.endereco_cep || '',
          cnh_numero: driver.cnh_numero || '',
          cnh_categoria: driver.cnh_categoria || '',
          cnh_vencimento: formatDateForInput(driver.cnh_vencimento),
          cnh_primeira_habilitacao: formatDateForInput(driver.cnh_primeira_habilitacao),
          cnh_pontos_atuais: driver.cnh_pontos_atuais || 0,
          foto_motorista: driver.foto_motorista || '',
          foto_cnh_frente: driver.foto_cnh_frente || '',
          foto_cnh_verso: driver.foto_cnh_verso || '',
          contato_emergencia_nome: driver.contato_emergencia_nome || '',
          contato_emergencia_telefone: driver.contato_emergencia_telefone || '',
          contato_emergencia_parentesco: driver.contato_emergencia_parentesco || '',
          data_admissao: formatDateForInput(driver.data_admissao),
          data_demissao: formatDateForInput(driver.data_demissao),
          salario: driver.salario || 0,
          comissao_percentual: driver.comissao_percentual || 0,
          banco: driver.banco || '',
          agencia: driver.agencia || '',
          conta: driver.conta || '',
          pix_chave: driver.pix_chave || '',
          observacoes: driver.observacoes || '',
          status: driver.status || 'ativo',
          avaliacao_comportamental: driver.avaliacao_comportamental || '',
          numero_viagens_realizadas: driver.numero_viagens_realizadas || 0,
          quilometragem_total: driver.quilometragem_total || 0
        });
      } else {
        throw new Error(response.message || 'Erro ao carregar dados do motorista');
      }
    } catch (error: any) {
      console.error('Erro ao carregar motorista:', error);
      utils.showNotification(error.message || 'Erro ao carregar dados do motorista', { type: 'error' });
      navigate('/motoristas');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));

    if (errors[name as keyof DriverFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const formatCPF = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return cleaned
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({ ...prev, cpf: formatted }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, [e.target.name]: formatted }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome completo é obrigatório';
    }

    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    }

    if (!formData.celular.trim()) {
      newErrors.celular = 'Celular é obrigatório';
    }

    if (!formData.cnh_numero.trim()) {
      newErrors.cnh_numero = 'Número da CNH é obrigatório';
    }

    if (!formData.cnh_vencimento) {
      newErrors.cnh_vencimento = 'Validade da CNH é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const cleanFormData = (data: DriverFormData): DriverFormData => {
    const cleaned = { ...data };
    
    // Converter strings vazias em campos de data para undefined
    const dateFields: (keyof DriverFormData)[] = [
      'data_nascimento',
      'cnh_vencimento',
      'cnh_primeira_habilitacao',
      'data_admissao',
      'data_demissao'
    ];
    
    dateFields.forEach(field => {
      if (cleaned[field] === '') {
        (cleaned[field] as string | undefined) = undefined;
      }
    });
    
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // Limpar dados antes de enviar para a API
      const cleanedData = cleanFormData(formData);
      const response = await apiService.updateDriver(Number(id), cleanedData);
      
      if (response.success) {
        utils.showNotification('Motorista atualizado com sucesso!', { type: 'success' });
        navigate('/motoristas');
      } else {
        throw new Error(response.message || 'Erro ao atualizar motorista');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar motorista:', error);
      utils.showNotification(error.message || 'Erro ao atualizar motorista', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/motoristas');
  };

  if (isLoadingData) {
    return (
      <ViewLayout
        title="Carregando Motorista..."
        isLoading={true}
        onBack={handleCancel}
      >
        <div></div>
      </ViewLayout>
    );
  }

  return (
    <ViewLayout
      title="Editar Motorista"
      subtitle="Edição de Motorista"
      entityId={id}
      isLoading={isLoading}
      onBack={handleCancel}
    >
      <form onSubmit={handleSubmit}>
              {/* Informações Básicas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Informações Básicas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      className={`form-control ${errors.nome ? 'error' : ''}`}
                      placeholder="Digite o nome completo"
                    />
                    {errors.nome && <span className="error-message">{errors.nome}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">CPF *</label>
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleCpfChange}
                      className={`form-control ${errors.cpf ? 'error' : ''}`}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                    {errors.cpf && <span className="error-message">{errors.cpf}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Celular *</label>
                    <input
                      type="text"
                      name="celular"
                      value={formData.celular}
                      onChange={handlePhoneChange}
                      className={`form-control ${errors.celular ? 'error' : ''}`}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                    {errors.celular && <span className="error-message">{errors.celular}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">LID (WhatsApp)</label>
                    <input
                      type="text"
                      name="lid"
                      value={formData.lid || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="ID vinculado do WhatsApp"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Número da CNH *</label>
                    <input
                      type="text"
                      name="cnh_numero"
                      value={formData.cnh_numero}
                      onChange={handleInputChange}
                      className={`form-control ${errors.cnh_numero ? 'error' : ''}`}
                      placeholder="Número da CNH"
                    />
                    {errors.cnh_numero && <span className="error-message">{errors.cnh_numero}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Validade CNH *</label>
                    <input
                      type="date"
                      name="cnh_vencimento"
                      value={formData.cnh_vencimento}
                      onChange={handleInputChange}
                      className={`form-control ${errors.cnh_vencimento ? 'error' : ''}`}
                    />
                    {errors.cnh_vencimento && <span className="error-message">{errors.cnh_vencimento}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">RG</label>
                    <input
                      type="text"
                      name="rg"
                      value={formData.rg || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número do RG"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data de Nascimento</label>
                    <input
                      type="date"
                      name="data_nascimento"
                      value={formData.data_nascimento || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      name="telefone"
                      value={formData.telefone || ''}
                      onChange={handlePhoneChange}
                      className="form-control"
                      placeholder="(00) 0000-0000"
                      maxLength={15}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="email@exemplo.com"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Endereço
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Rua</label>
                    <input
                      type="text"
                      name="endereco_rua"
                      value={formData.endereco_rua || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome da rua"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Número</label>
                    <input
                      type="text"
                      name="endereco_numero"
                      value={formData.endereco_numero || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nº"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Complemento</label>
                    <input
                      type="text"
                      name="endereco_complemento"
                      value={formData.endereco_complemento || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Apto, bloco, etc."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Bairro</label>
                    <input
                      type="text"
                      name="endereco_bairro"
                      value={formData.endereco_bairro || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome do bairro"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cidade</label>
                    <input
                      type="text"
                      name="endereco_cidade"
                      value={formData.endereco_cidade || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome da cidade"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <input
                      type="text"
                      name="endereco_estado"
                      value={formData.endereco_estado || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">CEP</label>
                    <input
                      type="text"
                      name="endereco_cep"
                      value={formData.endereco_cep || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>
                </div>
              </div>

              {/* Documentação - CNH */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Documentação - CNH
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Categoria CNH</label>
                    <input
                      type="text"
                      name="cnh_categoria"
                      value={formData.cnh_categoria || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: AB"
                      maxLength={2}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Primeira Habilitação</label>
                    <input
                      type="date"
                      name="cnh_primeira_habilitacao"
                      value={formData.cnh_primeira_habilitacao || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Pontos Atuais</label>
                    <input
                      type="number"
                      name="cnh_pontos_atuais"
                      value={formData.cnh_pontos_atuais || 0}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              </div>

              {/* Contato de Emergência */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Contato de Emergência
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Nome</label>
                    <input
                      type="text"
                      name="contato_emergencia_nome"
                      value={formData.contato_emergencia_nome || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome completo"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      name="contato_emergencia_telefone"
                      value={formData.contato_emergencia_telefone || ''}
                      onChange={handlePhoneChange}
                      className="form-control"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Parentesco</label>
                    <input
                      type="text"
                      name="contato_emergencia_parentesco"
                      value={formData.contato_emergencia_parentesco || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: Pai, Mãe, Cônjuge"
                    />
                  </div>
                </div>
              </div>

              {/* Dados Profissionais */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Dados Profissionais
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Data de Admissão</label>
                    <input
                      type="date"
                      name="data_admissao"
                      value={formData.data_admissao || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data de Demissão</label>
                    <input
                      type="date"
                      name="data_demissao"
                      value={formData.data_demissao || ''}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Salário (R$)</label>
                    <input
                      type="number"
                      name="salario"
                      value={formData.salario || 0}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Comissão (%)</label>
                    <input
                      type="number"
                      name="comissao_percentual"
                      value={formData.comissao_percentual || 0}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Banco</label>
                    <input
                      type="text"
                      name="banco"
                      value={formData.banco || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome do banco"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Agência</label>
                    <input
                      type="text"
                      name="agencia"
                      value={formData.agencia || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número da agência"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Conta</label>
                    <input
                      type="text"
                      name="conta"
                      value={formData.conta || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número da conta"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Chave PIX</label>
                    <input
                      type="text"
                      name="pix_chave"
                      value={formData.pix_chave || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Chave PIX"
                    />
                  </div>
                </div>
              </div>

              {/* Estatísticas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Estatísticas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Viagens Realizadas</label>
                    <input
                      type="number"
                      name="numero_viagens_realizadas"
                      value={formData.numero_viagens_realizadas || 0}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Quilometragem Total</label>
                    <input
                      type="number"
                      name="quilometragem_total"
                      value={formData.quilometragem_total || 0}
                      onChange={handleInputChange}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Avaliação Comportamental */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Avaliação Comportamental
                </h3>
                
                <div className="form-group">
                  <label className="form-label">Avaliação</label>
                  <textarea
                    name="avaliacao_comportamental"
                    value={formData.avaliacao_comportamental || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Digite a avaliação comportamental do motorista"
                    rows={4}
                  />
                </div>
              </div>

              {/* Observações */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Observações
                </h3>
                
                <div className="form-group">
                  <label className="form-label">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Digite observações adicionais"
                    rows={4}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading"></span>
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Atualizar Motorista
                    </>
                  )}
                </button>
              </div>
      </form>
    </ViewLayout>
  );
};

export default DriverEdit;
